// Pre-existing complexity (CTA decision tree, banner/popover JSX) — not part of
// the data-layer migration's intended scope.
// fallow-ignore-file complexity
import { useMemo } from 'react';
import type { MouseEvent } from 'react';
import { Box, Paragraph, Link, Strong } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { getInboxLinkAsync } from '@/services/inbox-link';
import { isEmptyString } from '@/utils/string';
import { authStore } from '@/services/auth/store';
import { useIsOnboardingChecklistVisible } from '@/features/onboarding/hooks';
import { OnboardingViewId } from '@/features/onboarding';
import { DeliverySettings, useSampleWorkspace } from '@/features/workspaces';
import { DeliveryModePopover, useDeliveryModeState } from '@/features/delivery-mode';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import { useAnalytics } from '@/services/analytics';
import * as styles from './index.css';

export function DeliveryModeBanner() {
  const { track } = useAnalytics();
  const auth = useStore(authStore);
  const router = useRouter();

  const {
    workspace,
    isBuilder,
    builderMustUpgrade,
    isTestDeliveryMode,
    isDeliveryDisabled,
    isSendingDomainSetUp,
    isFromAddressSetUp,
    verificationRejected,
    verificationNeedsMoreInfo,
    sendingDomainsHref,
    cta,
    buildCtaHref,
  } = useDeliveryModeState();
  const workspaceId = workspace?.id ?? 0;

  const verificationStepHref = router.buildLocation({
    to: '/workspaces/$workspaceId/home',
    params: { workspaceId },
    search: { step: OnboardingViewId.AccountVerification },
  }).href;

  const { isSampleWorkspace } = useSampleWorkspace();
  const hasFeatureFlag = useFeatureFlag(FeatureFlag.TestDeliveryMode);

  const shouldShow = useMemo(() => {
    if (workspace == null) {
      return false;
    }

    // Don't show for sample workspace
    if (isSampleWorkspace(workspace.id)) {
      return false;
    }

    if (!hasFeatureFlag) {
      return false;
    }

    if (
      workspace.delivery_settings !== DeliverySettings.SendToOne &&
      workspace.delivery_settings !== DeliverySettings.Off
    ) {
      return false;
    }

    return true;
  }, [workspace, isSampleWorkspace, hasFeatureFlag]);

  // The on-hold copy points at the verification step, which exists only inside
  // the onboarding checklist — so it ships only where home renders that checklist.
  const isOnboardingChecklistVisible = useIsOnboardingChecklistVisible(auth.account);

  const userEmail = auth.user?.email ?? '';
  const { data: inboxLink } = useQuery({
    queryKey: ['verification-review-inbox-link', userEmail],
    queryFn: () => getInboxLinkAsync(userEmail),
    enabled: verificationNeedsMoreInfo && !isEmptyString(userEmail),
    staleTime: Infinity,
  });

  const handleOpenVerificationReviewInbox = (event: MouseEvent) => {
    event.preventDefault();
    if (inboxLink == null) {
      return;
    }
    track('account', 'verificationReviewInboxLinkClicked', {
      'MX Provider': inboxLink.provider,
      'Email Domain': inboxLink.email.split('@')[1] ?? '',
    });
    window.open(inboxLink.url, '_blank', 'noopener,noreferrer');
  };

  // Decides which copy the test-mode banner shows for non-Builder workspaces. The banner reflects
  // the user's actual next step rather than always pointing at sending-domain setup. Unresolved
  // account verification (rejected, needs-more-info) outranks sending-domain setup: domain work
  // can't get messages delivered while verification is blocked.
  const nonBuilderTestModeVariant = useMemo<
    'rejected' | 'add-domain' | 'add-from-address' | 'needs-more-info' | 'move-out-of-test-mode' | null
  >(() => {
    if (!isTestDeliveryMode || isBuilder) {
      return null;
    }
    if (verificationRejected) {
      return 'rejected';
    }
    if (verificationNeedsMoreInfo) {
      return 'needs-more-info';
    }
    if (!isSendingDomainSetUp) {
      return 'add-domain';
    }
    if (!isFromAddressSetUp) {
      return 'add-from-address';
    }
    return 'move-out-of-test-mode';
  }, [
    isTestDeliveryMode,
    isBuilder,
    isSendingDomainSetUp,
    isFromAddressSetUp,
    verificationNeedsMoreInfo,
    verificationRejected,
  ]);

  if (!shouldShow) {
    return null;
  }

  return (
    <DeliveryModePopover>
      {(popoverContext) => (
        <Box
          className={styles.deliveryModeBanner}
          ref={popoverContext.reference.setReference}
          {...popoverContext.reference.getReferenceProps()}
        >
          <Paragraph color="inherit">
            {isTestDeliveryMode && isBuilder && cta?.link != null && cta.route != null ? (
              <>
                Emails are delivered to your test users only.{' '}
                <Strong>
                  <Link href={buildCtaHref(cta)} variant="secondary" color="inherit">
                    {cta.link}
                  </Link>
                </Strong>{' '}
                {cta.text}
              </>
            ) : null}
            {nonBuilderTestModeVariant === 'add-domain' ? (
              <>
                <Strong>Email sending</Strong> is in <Strong>test mode</Strong>.{' '}
                <Link href={sendingDomainsHref} variant="secondary" color="inherit">
                  Add and verify a sending domain
                </Link>{' '}
                to reach your audience.
              </>
            ) : null}
            {nonBuilderTestModeVariant === 'add-from-address' ? (
              <>
                <Strong>Email sending</Strong> is in <Strong>test mode</Strong>.{' '}
                <Link href={sendingDomainsHref} variant="secondary" color="inherit">
                  Add a From address
                </Link>{' '}
                to your sending domain to reach your audience.
              </>
            ) : null}
            {nonBuilderTestModeVariant === 'rejected' ? (
              <>
                Your account was not approved because the email domain and website information you provided at signup
                does not align with our{' '}
                <Link
                  href="https://customer.io/legal/acceptable-use-policy"
                  variant="secondary"
                  color="inherit"
                  isExternal={true}
                >
                  Acceptable Use Policy
                </Link>
                . If you believe this is an error, please{' '}
                <Link
                  href="#"
                  variant="secondary"
                  color="inherit"
                  onClick={(event: MouseEvent) => {
                    event.preventDefault();
                    window.__hydra_sidebar__?.openSupportForm?.('verification_rejected_banner');
                  }}
                >
                  contact support
                </Link>
                .
              </>
            ) : null}
            {nonBuilderTestModeVariant === 'needs-more-info' ? (
              <>
                {/* Straight to the step that holds the questions and the reply box.
                      Without a checklist there is nowhere in-product to answer, so
                      those accounts keep being pointed at the email instead. */}
                {isOnboardingChecklistVisible ? (
                  <>
                    <Strong>Your account is on hold.</Strong> Messages can't be sent until it's verified.{' '}
                    <Link href={verificationStepHref} variant="secondary" color="inherit">
                      Verify your account
                    </Link>
                    .
                  </>
                ) : (
                  <>
                    We're reviewing your account, but additional information is needed. Check your inbox for an email
                    with the subject <Strong>"Action required to verify your Customer.io account"</Strong> for the next
                    steps.
                    {inboxLink != null ? (
                      <>
                        {' '}
                        <Link
                          href={inboxLink.url}
                          variant="secondary"
                          color="inherit"
                          isExternal={true}
                          onClick={handleOpenVerificationReviewInbox}
                          data-test-verification-review-inbox-link
                        >
                          Open in {inboxLink.label}
                        </Link>
                      </>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
            {nonBuilderTestModeVariant === 'move-out-of-test-mode' ? (
              <>
                <Strong>Email sending</Strong> is in <Strong>test mode</Strong>. Move out of test mode{' '}
                <Link
                  href={
                    router.buildLocation({
                      to: '/workspaces/$workspaceId/settings/edit',
                      params: { workspaceId },
                      search: { fragment: 'delivery-settings', enableMessageSending: true },
                    }).href
                  }
                  variant="secondary"
                  color="inherit"
                >
                  here
                </Link>
                .
              </>
            ) : null}
            {isDeliveryDisabled && builderMustUpgrade && cta?.link != null && cta.route != null ? (
              <>
                Message sending is disabled.{' '}
                <Link href={buildCtaHref(cta)} variant="secondary" color="inherit">
                  {cta.link}
                </Link>{' '}
                {cta.text}
              </>
            ) : null}
            {isDeliveryDisabled && !builderMustUpgrade ? (
              <>
                Message sending is disabled. Enable sending in your{' '}
                <Link
                  href={
                    router.buildLocation({
                      to: '/workspaces/$workspaceId/settings/edit',
                      params: { workspaceId },
                      search: { fragment: 'delivery-settings', enableMessageSending: true },
                    }).href
                  }
                  variant="secondary"
                  color="inherit"
                >
                  workspace settings
                </Link>
                .
              </>
            ) : null}
          </Paragraph>
        </Box>
      )}
    </DeliveryModePopover>
  );
}
